import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Fingerprint } from 'lucide-react';
import { ApiError, post } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  authenticatePasskey,
  cancelPasskeyCeremony,
  errorMessage,
  isPasskeyCancelled,
} from '../../lib/passkey.js';
import { Alert, FieldError, PasswordField } from '../../lib/ui.js';
import { Steps } from '../../lib/AuthShell.js';
import { t } from '../../i18n/index.js';

/** Step 1 password → pending session; step 2 passkey → full session. No skip path. */
export function AdminLoginPage() {
  const { me, refresh, expired, error: unreachable } = useAuth();
  const location = useLocation() as { state?: { email?: string } };
  const pending = me?.session?.kind === 'admin' && !me.session.mfa_verified;
  const [email, setEmail] = useState(location.state?.email ?? '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  const step1 = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/admin/login', { email, password, remember });
      setPassword('');
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'locked_out') {
        const secs = Number(err.details.retry_after_seconds ?? 900);
        setError(t('admin_login_locked', { minutes: Math.max(1, Math.ceil(secs / 60)) }));
      } else if (err instanceof ApiError && err.code === 'invalid_credentials') {
        const left = Number(err.details.failures_remaining ?? 0);
        setError(`${t('admin_login_invalid')} ${t('admin_login_failures_left', { n: left })}`);
      } else if (err instanceof ApiError && err.code === 'forbidden') {
        setError(t('admin_login_enrol_required'));
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  /** The passkey prompt. A dismissed prompt is a hint, never a banner, and never retries itself. */
  const step2 = async () => {
    setBusy(true);
    setError(null);
    setHint(null);
    try {
      await authenticatePasskey('/auth/admin/passkey/options', '/auth/admin/passkey/verify');
      await refresh();
    } catch (err) {
      if (isPasskeyCancelled(err)) setHint(t('passkey_cancel_hint'));
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  // Offer the passkey once when the step appears; after that the button is the only way to retry.
  const prompted = useRef(false);
  useEffect(() => {
    if (!pending) {
      prompted.current = false;
      return;
    }
    if (prompted.current) return;
    prompted.current = true;
    void step2();
    // Leaving the step (Back, or a route change) closes a prompt that is still open.
    return cancelPasskeyCeremony;
  }, [pending]);

  /** Drop the pending session (and its passkey challenge) and return to the password form. */
  const back = async (differentAccount: boolean) => {
    cancelPasskeyCeremony();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/logout');
    } catch {
      /* the cookie is cleared either way */
    }
    setPassword('');
    setHint(null);
    if (differentAccount) setEmail('');
    await refresh();
    setBusy(false);
  };

  return (
    <div className="auth-form">
      <h2 className="auth-title">{t('admin_login_title')}</h2>
      <Steps current={pending ? 2 : 1} labels={[t('password'), t('auth_step_passkey')]} />
      {expired && !pending && <Alert kind="info">{t('session_expired')}</Alert>}
      {unreachable && <Alert>{t('api_unreachable')}</Alert>}
      {pending ? (
        <div className="passkey-step">
          <div className="passkey-icon" aria-hidden="true">
            <Fingerprint size={34} />
          </div>
          <p className="auth-lead">{t('admin_login_step2')}</p>
          {me?.user?.email && <p className="muted auth-email">{me.user.email}</p>}
          <button type="button" className="big" onClick={() => void step2()} disabled={busy}>
            {busy ? t('passkey_signing_in') : t('passkey_sign_in')}
          </button>
          <FieldError id="passkey-error" message={error} />
          {hint && !error && (
            <p className="muted small-text passkey-hint" role="status">
              {hint}
            </p>
          )}
          <div className="auth-links">
            <button type="button" className="link" onClick={() => void back(false)}>
              {t('back')}
            </button>
            <button type="button" className="link" onClick={() => void back(true)}>
              {t('admin_login_other_account')}
            </button>
            <Link to="/forgot">{t('admin_login_forgot')}</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={step1} noValidate>
          <label>
            {t('email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </label>
          <PasswordField
            id="login-password"
            label={t('password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'login-error' : undefined}
          />
          <FieldError id="login-error" message={error} />
          <label className="inline remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            {t('admin_login_remember')}
          </label>
          <p className="muted small-text">{t('admin_login_remember_hint')}</p>
          <button type="submit" className="big" disabled={busy || !email || !password}>
            {t('continue')}
          </button>
          <div className="auth-links center">
            <Link to="/forgot">{t('admin_login_forgot')}</Link>
          </div>
        </form>
      )}
    </div>
  );
}
